/**
 * Simulateur d'économie TikTok Shop (9 %) vs CashLive Freemium (2,8 %)
 * Source TikTok Shop UE : commission standard 9 % depuis le 8 janv. 2026
 */

import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_COMMISSION_RATE } from "@/shared/constants/commission";

/** Commission TikTok Shop standard UE/FR — Seller University TikTok, à partir du 08/01/2026 */
export const TIKTOK_SHOP_STANDARD_RATE = 9;

const PRESETS_EUR = [1000, 5000, 10000, 25000] as const;

function formatEur(amount: number, locale: string): string {
  return amount.toLocaleString(locale, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
}

function formatEurPrecise(amount: number, locale: string): string {
  return amount.toLocaleString(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

const FeesSavingsSimulator: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("en")
    ? "en-GB"
    : i18n.language?.startsWith("es")
      ? "es-ES"
      : "fr-FR";

  const [monthlySales, setMonthlySales] = useState(5000);

  const calc = useMemo(() => {
    const sales = Math.max(0, Number.isFinite(monthlySales) ? monthlySales : 0);
    const tiktokFees = (sales * TIKTOK_SHOP_STANDARD_RATE) / 100;
    const cashliveFees = (sales * DEFAULT_COMMISSION_RATE) / 100;
    const monthlySavings = Math.max(0, tiktokFees - cashliveFees);
    return {
      monthlySavings,
      yearlySavings: monthlySavings * 12,
    };
  }, [monthlySales]);

  return (
    <div className="mx-auto max-w-3xl">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 rounded-xl border border-border bg-surface px-3 py-2.5">
        <label htmlFor="fees-sim-sales" className="text-xs font-medium text-foreground-secondary shrink-0 whitespace-nowrap">
          {t("landing.feesSimulator.salesLabel")}
        </label>

        <div className="relative w-full sm:w-28 shrink-0">
          <input
            id="fees-sim-sales"
            type="number"
            min={0}
            step={100}
            value={monthlySales}
            onChange={(e) => setMonthlySales(Number(e.target.value) || 0)}
            className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 pr-7 text-sm font-semibold text-foreground tabular-nums focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-foreground-secondary">
            €
          </span>
        </div>

        <div className="hidden sm:flex flex-wrap gap-1 shrink-0">
          {PRESETS_EUR.map((amount) => (
            <button
              key={amount}
              type="button"
              onClick={() => setMonthlySales(amount)}
              className={`rounded-md px-1.5 py-1 text-[10px] font-medium transition-colors ${
                monthlySales === amount
                  ? "bg-primary text-white"
                  : "bg-background-tertiary text-foreground-secondary hover:text-foreground"
              }`}
            >
              {formatEur(amount, locale)}
            </button>
          ))}
        </div>

        <div className="sm:ml-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm">
          <span className="text-foreground-secondary">
            {t("landing.feesSimulator.youSave")}{" "}
            <strong className="text-primary tabular-nums">
              {formatEurPrecise(calc.monthlySavings, locale)}
            </strong>
            <span className="text-foreground-secondary/70">/{t("landing.feesSimulator.perMonthShort")}</span>
          </span>
          <span className="hidden sm:inline text-border">·</span>
          <span className="text-foreground-secondary">
            {t("landing.feesSimulator.yearlyLabel")}{" "}
            <strong className="text-success tabular-nums">
              {formatEurPrecise(calc.yearlySavings, locale)}
            </strong>
          </span>
        </div>
      </div>

      <p className="mt-1.5 text-[10px] leading-snug text-foreground-secondary/70 text-center">
        {t("landing.feesSimulator.source")}
      </p>
    </div>
  );
};

export default FeesSavingsSimulator;
